import React from 'react'
import {Link, LinkProps, Stack, StackProps, Text, TextProps, useMultiStyleConfig} from '@chakra-ui/react'
import {useAnalytics} from '_customHooks'
import {FooterColumn, FooterColumnLink} from '_types'

interface FooterColumnProps extends FooterColumn {
    isAccordion?: boolean
}

/**
 * @name FooterColumnComponent
 *
 * @component
 *
 * @description
 * Renders a footer column containing a title and a list of items.
 *
 * @param {FooterColumnProps} props - The properties object.
 * @param {string} props.title - The title of the list.
 * @param {Array<FooterColumnLink>} props.links - A list of items.
 * @param {boolean} [props.isAccordion] - Indicates if the list should be rendered as an accordion.
 *
 * @returns {React.FC<FooterColumnProps>} Rendered `FooterColumnComponent`.
 */
const FooterColumnComponent: React.FC<FooterColumnProps> = ({title, links, isAccordion = false}) => {
    const styles = useMultiStyleConfig('FooterSection') as Record<string, StackProps & TextProps & LinkProps>

    const {analyticsEventSizeChartClick, analyticsEventExclusiveServicesClick, analyticsEventExclusiveServicesAppointmentClick} = useAnalytics()

    const handleClick = (itemId: string, link?: string) => {
        if (itemId === 'size_guide') {
            analyticsEventSizeChartClick({
                category: 'Size Chart Footer',
                action: 'open',
            })
        }

        if (itemId === 'exclusive_services') {
            analyticsEventExclusiveServicesClick({
                action: 'footer',
                label: window.location.href,
            })
        }

        if (itemId === 'exclusive_service_appointment') {
            analyticsEventExclusiveServicesAppointmentClick({
                action: 'footer',
                label: window.location.href,
            })
        }

        if (itemId === 'cookie-settings' && link) {
            eval(link)
        }
    }

    return (
        <Stack {...styles.section} data-testid={`footer-section-${title}`}>
            {!isAccordion && (
                <Text as="h2" {...styles.sectionTitle}>
                    {title}
                </Text>
            )}

            <Stack {...styles.sectionContent}>
                {links.map((item: FooterColumnLink) => (
                    <Link
                        variant="hoverUnderline"
                        {...styles.sectionItem}
                        key={item.name}
                        href={item.href.includes('()') ? '#' : item.href}
                        onClick={() => handleClick(item.id, item.href.includes('()') ? item.href : undefined)}
                        isExternal={item.isExternal}
                        aria-label={item.ariaLabel}
                    >
                        {item.name}
                    </Link>
                ))}
            </Stack>
        </Stack>
    )
}

export default FooterColumnComponent
